import type {
  DragCancelEvent,
  DragEndEvent,
  DragMoveEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import * as React from 'react';

import type { GridDndHandlers } from './grid-dnd-scope';
import { externalGridDragItem, isExternalGridDrag } from './grid-dnd-scope';
import type { GridGestureCore } from './grid-gesture';
import type { useGridDrag } from './use-grid-drag';
import type { useExternalDrag } from './use-external-drag';
import { GridItemIdContext } from './sortable-grid-item';
import type { Omittable } from "../../lib/props";

/** A grid's subgrid-zone spec: mounts the grid element as a droppable other
 *  grids' gestures yield to. `accepts` gates eligibility ONCE per drag; the
 *  container's own tile is always rejected (self-drop). */
export type GridSubgridZone = Omittable<{
  id: string;
  accepts?: (e: { item: unknown; activeId: string }) => boolean;
}>;

/**
 * Zone eligibility for one drag, decided ONCE at drag start: a subgrid zone
 * may only enable when the drop could actually commit somewhere. Self-drops
 * never qualify; the grid's OWN child reordering qualifies iff manageable
 * (nested internal reorders preview only while `over` is the grid's own
 * zone, so the zone must be live during its own drags); a payload drag
 * qualifies iff the grid is manageable, has an `onExternalDrop` to commit
 * through, and the consumer's `accepts` agrees; a payload-less foreign drag
 * never qualifies — enabling the zone for it would make the page grid yield
 * to a zone whose grid never started a gesture, so the drop would commit
 * nowhere and silently revert.
 */
export function resolveZoneEligibility(args: {
  selfDrop: boolean;
  ownsActive: boolean;
  canManage: boolean;
  hasPayload: boolean;
  hasExternalDrop: boolean;
  accepts: boolean;
}): boolean {
  if (args.selfDrop) return false;
  if (args.ownsActive) return args.canManage;
  if (args.hasPayload) {
    return args.canManage && args.hasExternalDrop && args.accepts;
  }
  return false;
}

/**
 * Route scope-dispatched events — classified ONCE, at drag start, and held
 * STICKY for the whole drag: id ownership first (a grid that owns the active
 * id routes internal even when the drag carries a payload, so a nested
 * child's drag is internal to its home grid and external to every other
 * grid); otherwise a payload (palette/tile item) routes external; otherwise
 * the drag is not ours. Move/end/cancel route purely by the stored
 * classification — re-classifying from the live layout would let a mid-drag
 * layout mutation (Cmd+Z, a server-driven removal) flip the route between
 * start and end, sending the end event to a hook whose mode guard neither
 * commits nor cancels: a stranded gesture. A grid that mounts mid-drag never
 * saw the start → ref null → inert. ONE stable handler object registered
 * once — it reads the live hook handlers through a ref (stable-event
 * pattern), so no per-render re-registration churn.
 *
 * Also owns per-drag zone eligibility (`resolveZoneEligibility`, computed in
 * the same onDragStart): the returned `zoneEligible` gates the shell's
 * droppable — a disabled droppable never appears in `over`, so rejected
 * drags fall through to the outer grid's preview automatically.
 */
export function useScopedRouting(
  core: GridGestureCore,
  {
    zone,
    hasExternalDrop,
    drag,
    external,
  }: {
    zone: GridSubgridZone | undefined;
    hasExternalDrop: boolean;
    drag: ReturnType<typeof useGridDrag>;
    external: ReturnType<typeof useExternalDrag>;
  },
): { handlers: GridDndHandlers; zoneEligible: boolean } {
  // The grid tile this grid renders inside (nested grids only) — a drag of
  // that tile is a self-drop into its own zone and never eligible.
  const hostItemId = React.useContext(GridItemIdContext);

  const [zoneEligible, setZoneEligible] = React.useState(false);
  const zoneRef = React.useRef(zone);
  const hostItemIdRef = React.useRef(hostItemId);
  const canManageRef = React.useRef(core.canManage);
  const hasExternalDropRef = React.useRef(hasExternalDrop);
  React.useLayoutEffect(() => {
    zoneRef.current = zone;
    hostItemIdRef.current = hostItemId;
    canManageRef.current = core.canManage;
    hasExternalDropRef.current = hasExternalDrop;
  });

  const routingRef = React.useRef({ drag, external });
  React.useLayoutEffect(() => {
    routingRef.current = { drag, external };
  });
  const activeRouteRef = React.useRef<'internal' | 'external' | null>(null);

  const { layoutRef } = core;
  const handlers = React.useMemo<GridDndHandlers>(() => {
    const ownsActive = (event: { active: { id: string | number } }) =>
      layoutRef.current.some((i) => i.id === String(event.active.id));
    return {
      onDragStart: (event: DragStartEvent) => {
        const owns = ownsActive(event);
        const hasPayload = isExternalGridDrag(event);
        const route = owns ? 'internal' : hasPayload ? 'external' : null;
        activeRouteRef.current = route;
        // Zone eligibility: computed ONCE per drag, and only when the drop
        // could actually route somewhere — a zone that enables for a drag
        // no grid can commit swallows the drop (the page grid yields, the
        // zone grid never started a gesture, nothing commits).
        const z = zoneRef.current;
        if (z) {
          const activeId = String(event.active.id);
          setZoneEligible(
            resolveZoneEligibility({
              // The container's own tile dragged over its own zone.
              selfDrop:
                hostItemIdRef.current != null &&
                activeId === hostItemIdRef.current,
              ownsActive: owns,
              canManage: canManageRef.current,
              hasPayload,
              hasExternalDrop: hasExternalDropRef.current,
              accepts: hasPayload
                ? (z.accepts?.({
                    item: externalGridDragItem(event),
                    activeId,
                  }) ?? true)
                : false,
            }),
          );
        }
        const routes = routingRef.current;
        if (route === 'internal') return routes.drag.onDragStart(event);
        if (route === 'external') return routes.external.onDragStart(event);
      },
      onDragMove: (event: DragMoveEvent) => {
        const routes = routingRef.current;
        if (activeRouteRef.current === 'internal') return routes.drag.onDragMove(event);
        if (activeRouteRef.current === 'external') return routes.external.onDragMove(event);
      },
      onDragEnd: (event: DragEndEvent) => {
        setZoneEligible(false);
        const route = activeRouteRef.current;
        activeRouteRef.current = null;
        const routes = routingRef.current;
        if (route === 'internal') return routes.drag.onDragEnd(event);
        if (route === 'external') return routes.external.onDragEnd(event);
      },
      onDragCancel: (_event: DragCancelEvent) => {
        setZoneEligible(false);
        const route = activeRouteRef.current;
        activeRouteRef.current = null;
        const routes = routingRef.current;
        if (route === 'internal') return routes.drag.onDragCancel();
        if (route === 'external') return routes.external.onDragCancel();
      },
    };
  }, [layoutRef]);

  return { handlers, zoneEligible };
}
